import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Img from 'gatsby-image';
import Swiper from 'react-id-swiper';
import Icon from '@utility/Icon'
import bodyScroll from '@helpers/bodyScroll';
import cn from 'classnames';
import InViewport from '@components/InViewport'
import styles from './styles';

const GalleryCard = ({
  data : {
    caption,
    date,
    cover,
    images,
    title,
  }
}) => {
  const cardRef = useRef();
  const galleryRef = useRef();
  const [isOpen, setOpen] = useState(false);
  const [isSafari, setSafari] = useState(false);
  const [swiper, setSwiper] = useState(null);
  const [updateSwiper, setUpdateSwiper] = useState(0);
  const [counter, setCounter] = useState(1);

  useEffect(() => {
    if (typeof window != undefined) {
      setSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
    }

    window.addEventListener('resize', () => {
      if (galleryRef.current) {
        updateGalleryDimensions();
      }
    });
  }, []);

  useEffect(() => {
    if (swiper !== null) {
      swiper.on('slideChange', () => {
        setCounter(swiper.realIndex + 1);
      });
    }
  }, [swiper]);

  useEffect(() => {
    if (swiper !== null) {
      // Wait for resize of card so that swiper can update the size of the gallery
      setTimeout(() => {
        swiper.update();
      }, 700); // 700ms is the timing for the gallery to open
    }
  }, [swiper, updateSwiper]);

  // Initially set position of the gallery.
  const initialGalleryStyles = (rect) => {
    galleryRef.current.style.top = `${rect.y}px`;
    galleryRef.current.style.left = `${rect.x}px`;
    galleryRef.current.style.width = `${rect.width}px`;
  }

  const updateGalleryDimensions = () => {
    galleryRef.current.style.height = `${window.innerHeight}px`;
    galleryRef.current.style.width = `${window.innerHeight * 2/3}px`;
  }

  const openGallery = () => {
    if(cardRef.current && galleryRef.current) {
      bodyScroll.lock();
      setOpen(true);
      initialGalleryStyles(cardRef.current.getBoundingClientRect());

      if (isSafari) {
        galleryRef.current.style.transition = 'none';
      }


      // Set new properties so that the gallery transitions into its full height state.
      setTimeout(() => {
        galleryRef.current.style.top = `0px`;
        galleryRef.current.style.left = `50%`;
        galleryRef.current.style.transform = `translate3d(-50%, 0, 0)`;
        updateGalleryDimensions();
        setUpdateSwiper(updateSwiper + 1);
      }, 1);
    }
  }

  const closeGallery = () => {
    if(cardRef.current && galleryRef.current) {
      bodyScroll.unlock();

      cardRef.current.classList.add('opacity-0');

      setTimeout(() => {
        initialGalleryStyles(cardRef.current.getBoundingClientRect());
        cardRef.current.classList.remove('opacity-0');
        galleryRef.current.setAttribute('style', '');
        swiper.destroy();
        setOpen(false);
      }, 300);
    }
  };

  const prevSlide = () => {
    if (swiper !== null) {
      swiper.slidePrev();
    }
  }

  const nextSlide = () => {
    if (swiper !== null) {
      swiper.slideNext();
    }
  }

  const params = {
    spaceBetween: 16,
    loop: images.length > 1 ? true : false,
    lazy: {
      loadPrevNext: true,
    },
    navigation: {
      nextEl: `.${styles.next}`,
      prevEl: `.${styles.prev}`,
    },
    grabCursor: images.length > 1 ? true : false,
    speed: 600,
  }

  return (
    <InViewport
      className={styles.container}
    >
      <div ref={cardRef} className={styles.card} onClick={!isOpen ? openGallery : undefined}>
        <div className={cn(styles.imageWrapper, {
          [styles.active]: isOpen,
        })}>
          <div ref={galleryRef} className={styles.galleryWrapper}>
            <Img
              fluid={[
                cover.asset.fluid,
              ]}
              className={cn(styles.image, {
                [styles.noDelay]: isOpen & isSafari
              })}
              objectFit="cover"
              alt={cover.altText}
              placeholderClassName="gatsby-image-placeholder"
            />
              <div className={styles.gallery}>
                {isOpen && (
                  <>
                    <Swiper
                      {...params}
                      getSwiper={setSwiper}
                    >
                      {images.map((image, i) => (
                        <div key={image._key}>
                          <Img
                            fluid={[
                              image.asset.fluid,
                            ]}
                            className={styles.galleryImage}
                            objectFit="cover"
                            alt={image.altText}
                            imgStyle={{
                              opacity: 1,
                            }}
                            loading="eager"
                            placeholderClassName="gatsby-image-placeholder"
                          />
                        </div>
                      ))}
                    </Swiper>
                    { images.length > 1 && (
                      <>
                        <Icon
                          name="chevron-left"
                          color="grey"
                          className={styles.prev}
                          onClick={prevSlide}
                        />
                        <Icon
                          name="chevron-right"
                          color="grey"
                          className={styles.next}
                          onClick={nextSlide}
                        />
                      </>
                    )}

                  </>
                )}
              </div>
          </div>
          {isOpen && (
            <>
              <Icon
                name="close"
                color="white"
                className={styles.close}
                onClick={closeGallery}
              />
              <div className={styles.counter}>
                {counter} / {images.length}
              </div>
            </>
          )}
          {caption && (
            <div className={styles.caption}>
              <p className="type-160 text-white">{caption}</p>
            </div>
          )}
        </div>
        <div className={styles.inner}>
          <div className={styles.content}>
            <p className="mb-1 type-050-sans opacity-50">{date}</p>
            <p className={styles.title}>{title}</p>
          </div>
        </div>
      </div>
    </InViewport>
  );
};

GalleryCard.propTypes = {
  data: PropTypes.shape({
    caption: PropTypes.string,
    category: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    date: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
    title: PropTypes.string.isRequired,
  }),
};

GalleryCard.defaultProps = {
  data: {
    caption: '',
  }
};

export default GalleryCard;
